import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { SEO } from "../components/site/SEO";
import { ContactForm } from "../components/site/ContactForm";
import { useContent } from "../context/ContentContext";

export default function Home() {
  const { content } = useContent();
  const home = content.home;
  const HOME_SERVICES = (content.services || []).slice(0, 6);
  return (
    <SiteLayout>
      <SEO
        title="Fourbees Consultancy — Doorstep Dairy Business Consultancy in the UK"
        description="Specialist consultancy for the UK doorstep dairy industry. Over 15 years of hands-on experience helping independent dairies and small businesses operate better and grow sustainably."
        path="/"
      />

      {/* HERO — editorial, typography-led */}
      <section
        className="relative bg-cream py-24 md:py-32 lg:py-40 overflow-hidden"
        data-testid="home-hero"
      >
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-8">
              <span className="eyebrow text-navy/70" data-testid="home-hero-eyebrow">
                {home.heroEyebrow}
              </span>
              <h1
                className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] font-extrabold text-navy leading-[0.98] tracking-[-0.03em] mt-6"
                data-testid="home-hero-headline"
              >
                {home.heroHeadline}
                <br />
                <span className="text-gold">{home.heroHeadlineAccent}</span>
              </h1>
              <p
                className="font-body text-base md:text-lg text-charcoal/75 leading-relaxed mt-8 max-w-2xl"
                data-testid="home-hero-sub"
              >
                {home.heroSub}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="btn-primary" data-testid="home-cta-primary">
                  Let's Talk About Your Business
                  <ArrowRight size={16} strokeWidth={2.5} />
                </Link>
                <Link to="/services" className="btn-secondary" data-testid="home-cta-secondary">
                  Explore Our Services
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 lg:border-l lg:border-light-grey lg:pl-10">
              <div className="section-number mb-3">— Credibility</div>
              <p className="font-heading text-2xl md:text-3xl text-navy font-bold leading-tight">
                {home.credibilityHeadline}
              </p>
              <p className="font-body text-sm text-charcoal/70 mt-4 leading-relaxed">
                {home.credibilityBody}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO — "We've Been There" */}
      <section className="bg-cream border-t border-light-grey py-24 md:py-28" data-testid="home-intro">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">01 — Introduction</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight">
                We've been there. <br />We understand.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <div className="space-y-5 font-body text-base md:text-lg text-charcoal/80 leading-relaxed">
                <p>
                  Running a doorstep dairy takes more than delivering milk. There's
                  route planning, customer service, invoicing, administration, marketing
                  and the constant challenge of finding new customers.
                </p>
                <p>
                  At Fourbees Consultancy, we understand those pressures because we've
                  experienced them first-hand.
                </p>
                <p className="text-navy font-semibold">
                  We provide straightforward, practical support that helps independent
                  dairies spend less time dealing with problems and more time building
                  successful businesses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW — editorial list */}
      <section
        id="services"
        className="bg-cream border-t border-light-grey py-24 md:py-28"
        data-testid="home-services"
      >
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <div className="section-number mb-3">02 — What we do</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight">
                Six focused services. <br />No filler.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="font-body text-charcoal/70 leading-relaxed">
                Each piece of work is grounded in real industry experience. Pick what
                you need, or we'll look at the whole picture together.
              </p>
            </div>
          </div>

          <div className="divide-y divide-light-grey border-t border-light-grey">
            {HOME_SERVICES.map((s) => (
              <Link
                key={s.slug}
                to="/services"
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-7 md:py-9 hover:bg-white transition-colors px-2 -mx-2"
                data-testid={`home-service-${s.slug}`}
              >
                <div className="md:col-span-1 section-number">{s.index}</div>
                <div className="md:col-span-4">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-navy group-hover:text-gold-dark transition-colors leading-tight">
                    {s.title}
                  </h3>
                </div>
                <div className="md:col-span-6">
                  <p className="font-body text-sm md:text-base text-charcoal/70 leading-relaxed">
                    {s.short}
                  </p>
                </div>
                <div className="md:col-span-1 flex md:justify-end items-start text-navy/40 group-hover:text-navy transition-colors">
                  <ArrowUpRight size={20} strokeWidth={2} />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/services" className="link-arrow" data-testid="home-services-all">
              See all services <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY FOURBEES — on navy */}
      <section className="bg-navy text-cream py-24 md:py-28" data-testid="home-why">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">03 — Why Fourbees</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-cream leading-[1.05] tracking-tight">
                Industry knowledge that makes the difference.
              </h2>
              <p className="font-body text-cream/70 mt-6 leading-relaxed">
                Most consultants have read about the industry. We've worked in it. That
                shows up in the quality of the advice.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                {[
                  { title: "Genuine hands-on experience", body: "15+ years inside doorstep dairy operations. We've done the early starts, the awkward customer calls and the Sunday admin." },
                  { title: "Personal service", body: "You work with Simon directly. Not an account manager. Not a team you never meet." },
                  { title: "Practical, realistic advice", body: "No theoretical frameworks. Solutions that actually fit how your business works day to day." },
                  { title: "Understanding of independent businesses", body: "We know the pressures family-run and independent operators are under — and the margins they're protecting." },
                  { title: "Respect for traditional service", body: "Grow the business without losing what makes it worth running in the first place." },
                  { title: "Support for British farming", body: "We care about preserving traditional doorstep delivery and the farmers who keep it supplied." },
                ].map((v, i) => (
                  <div key={i} data-testid={`home-why-${i}`}>
                    <div className="section-number mb-2">0{i + 1}</div>
                    <h3 className="font-heading text-lg md:text-xl font-bold text-cream mb-2 leading-snug">
                      {v.title}
                    </h3>
                    <p className="font-body text-sm text-cream/70 leading-relaxed">{v.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CTA — grow your round */}
      <section className="bg-cream py-24 md:py-28" data-testid="home-grow-cta">
        <div className="container-site">
          <div className="border border-light-grey bg-white p-10 md:p-16 rounded-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="section-number mb-3">04 — Grow your round</div>
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight mb-5">
                  Your next customer could be <br className="hidden md:block" />
                  just around the corner.
                </h2>
                <p className="font-body text-charcoal/70 leading-relaxed max-w-xl">
                  We help independent dairies attract new local customers through professional
                  websites, Facebook content, community-group engagement and targeted local
                  advertising. No vanity metrics — just enquiries landing in your inbox.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link to="/dairy-growth" className="btn-gold" data-testid="home-grow-cta-btn">
                  Grow Your Milk Round
                  <ArrowRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING + CONTACT */}
      <section
        id="contact"
        className="bg-cream border-t border-light-grey py-24 md:py-28"
        data-testid="home-contact"
      >
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">05 — Start a conversation</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight mb-6">
                Whether it's a round review or a full rebuild — we're here.
              </h2>
              <p className="font-body text-charcoal/75 leading-relaxed mb-8">
                Whether you need help finding new customers, improving your delivery routes,
                or getting your business organised, we're here to help. One honest conversation
                is a good place to start.
              </p>
              <div className="space-y-3 text-sm font-body">
                <div className="flex items-start gap-3">
                  <span className="text-gold">·</span>
                  <span>Free initial conversation, no commitment.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-gold">·</span>
                  <span>Direct reply from Simon within one working day.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-gold">·</span>
                  <span>Realistic advice — even if it's not to work with us.</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm
                heading="Tell us about your business."
                intro="A short note is fine — we'll follow up to understand the detail."
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
